import { Domain } from '@pushly/models/lib/structs/domain'
import { DomainDto, INotificationAction } from '../dtos/domain.dto'
import { NotificationAction } from '@pushly/models/lib/structs/notification-action'
import {
    FcmSendIntegrationApiVersion,
    IAbstractNativeApnsSendIntegration,
    IAbstractNativeFcmSendIntegration,
    IWebApnsSendIntegration,
} from '../interfaces/send-integrations'
import { WebApnsSendIntegrationConfig } from '@pushly/models/lib/structs/send-integration-configurations/web-apns-send-integration-config'
import { NativeApnsSendIntegrationConfig } from '@pushly/models/lib/structs/send-integration-configurations/native-apns-send-integration-config'
import { SendIntegrationPrivateKeyType } from '../enums/send-integration-private-key-type.enum'
import { NativeFcmSendIntegrationConfig } from '@pushly/models/lib/structs/send-integration-configurations/native-fcm-send-integration-config'
import {
    NativeLegacyFcmConfiguration,
    NativeV1FcmConfiguration,
} from '@halo/models/send-integration-configurations/native-fcm-configurations.model'
import { isJSONObjectType } from '@pushly/aqe/lib/utils/object'
import { DomainBuilder } from '@pushly/models/lib/builders/domain-builder'
import { NotificationActionBuilder } from '@pushly/models/lib/builders/notification-action-builder'
import { DomainUtmParams } from '@pushly/models/lib/structs/domain-utm-params'
import { DomainUtmParamsBuilder } from '@pushly/models/lib/builders/domain-utm-params-builder'
import { WebApnsSendIntegrationConfigBuilder } from '@pushly/models/lib/builders/web-apns-send-integration-config-builder'
import { NativeApnsSendIntegrationConfigBuilder } from '@pushly/models/lib/builders/native-apns-send-integration-config-builder'
import {
    NativeP12ApnsConfiguration,
    NativeP8ApnsConfiguration,
} from '@halo/models/send-integration-configurations/native-apns-configurations.model'
import { NativeFcmSendIntegrationConfigBuilder } from '@pushly/models/lib/builders/native-fcm-send-integration-config-builder'
import { DomainGlobalPromptSettings } from '@pushly/models/lib/structs/domain-global-prompt-settings'
import { GlobalPromptReferrerConditions } from '@pushly/models/lib/structs/global-prompt-referrer-conditions'
import { DomainEcommConfiguration } from '@pushly/models/lib/structs/domain-ecomm-configuration'
import { DomainFrequencyCaps } from '@pushly/models/lib/structs/domain-frequency-caps'
import { DomainFrequencyCapsBuilder } from '@pushly/models/lib/builders/domain-frequency-caps-builder'
import { IFrequencyCap, IFrequencyCapsConfig } from '@halo/interfaces/frequency-caps-config'
import { DomainFrequencyCap } from '@pushly/models/lib/structs/domain-frequency-cap'
import { DomainFrequencyCapBuilder } from '@pushly/models/lib/builders/domain-frequency-cap-builder'
import { parseArrayOrNull } from '@halo/_utils/array'
import { NotificationActionType } from '@pushly/aqe/lib/enums/notification-action-type'
import { SendIntegrationPrivateKey } from '@pushly/models/lib/structs/send-integration-configurations/send-integration-private-key'
import { SendIntegrationPushPackage } from '@pushly/models/lib/structs/send-integration-configurations/send-integration-push-package'
import { NotificationFrequencyCapsConfig } from '@pushly/models/lib/structs/notification-frequency-caps-config'
import { NotificationFrequencyCapsConfigBuilder } from '@pushly/models/lib/builders/notification-frequency-caps-config-builder'
import { IGlobalPromptReferrerConditions } from '@halo/interfaces/global-prompt-referrer-conditions'
import { PromptReferrerAllowType } from '@halo/enums/prompt-referrer-allow-type'
import { Feed, MutableFeed } from '@pushly/models/lib/structs/feeds/feed'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import { AsapFeedSchedule, MutableAsapFeedSchedule } from '@pushly/models/lib/structs/feeds/asap-feed-schedule'
import { FixedFeedSchedule, MutableFixedFeedSchedule } from '@pushly/models/lib/structs/feeds/fixed-feed-schedule'
import { Industry, MutableIndustry } from '@pushly/models/lib/structs/industry'
import { enumFromValue } from '@pushly/models'
import { MutableFeedConfigurationFieldOptions } from '@pushly/models/lib/structs/feeds/feed-configuration-field-options'
import { DomainDuplicateTimeWindow } from '@pushly/models/lib/structs/domains'
import { isObject } from '@halo/_utils/object'

// Hydration Exception

type Class = new (...args: any[]) => any
class HydrationException extends Error {
    public constructor(value: any, model: Class, message?: string) {
        super(message ?? `${model.name} cannot parse and hydrate from parameter type passed: ${JSON.stringify(value)}`)
    }
}

// Domain Model Upgrade Utilities

export function hydrateDomainModel(domain: DomainDto | Domain | DomainBuilder | any): Domain {
    const builder = Domain.Builder
    if (domain instanceof Domain || domain instanceof DomainBuilder || (isObject(domain) && 'accountId' in domain)) {
        builder
            .setId(domain.id)
            .setName(domain.name)
            .setAccountId(domain.accountId)
            .setAccountName(domain.accountName)
            .setAccountFlags(domain.accountFlags)
            .setAddedNotificationQueryParams(domain.addedNotificationQueryParams)
            .setCreatedAt(domain.createdAt)
            .setCreatedBy(domain.createdBy)
            .setCreatedByUsername(domain.createdByUsername)
            .setCollapseKeyType(domain.collapseKeyType)
            .setCurrency(domain.currency!)
            .setCurrencyCode(domain.currencyCode)
            .setDefaultNotificationActionLabels(domain.defaultNotificationActionLabels)
            .setDefaultNotificationActions(
                domain.defaultNotificationActions?.map((action: Record<string, any>) =>
                    hydrateNotificationActionModelFromDto(action),
                ) ?? null,
            )
            .setDefaultNotificationTtlSeconds(domain.defaultNotificationTtlSeconds)
            .setDeliveryThrottleMaxSeconds(domain.deliveryThrottleMaxSeconds)
            .setDuplicateTimeWindow(domain.duplicateTimeWindow)
            .setDefaultIconUrl(domain.defaultIconUrl)
            .setDefaultBadgeUrl(domain.defaultBadgeUrl)
            .setDisplayMeta(domain.displayMeta)
            .setDisplayName(domain.displayName)
            .setDomainKey(domain.domainKey)
            .setFcmServerKey(domain.fcmServerKey)
            .setFlags(Array.from(domain.flags))
            .setFrequencyCaps(domain.frequencyCaps)
            .setNotificationFrequencyCaps(domain.notificationFrequencyCaps)
            .setLandingUrlDomainWhitelist(domain.landingUrlDomainWhitelist)
            .setApnsConfiguration(hydrateWebApnsConfigurationModel(domain.apnsConfiguration))
            .setNativeApnsConfiguration(hydrateNativeApnsConfigurationModel(domain.nativeApnsConfiguration))
            .setNativeFcmConfiguration(hydrateNativeFcmConfigurationModel(domain.nativeFcmConfiguration))
            .setNotificationFieldSettings(domain.notificationFieldSettings ?? null)
            .setProdDomainId(domain.prodDomainId)
            .setRoutingKey(domain.routingKey)
            .setSdkEventOnlyDomains(Array.from(domain.sdkEventOnlyDomains))
            .setSenderId(domain.senderId)
            .setStatus(domain.status)
            .setStatusId(domain.statusId)
            .setTimeZone(domain.timeZone)
            .setUpdatedAt(domain.updatedAt)
            .setUpdatedBy(domain.updatedBy)
            .setUpdatedByUsername(domain.updatedByUsername)
            .setUtmParams(domain.utmParams)
            .setVapidPublicKey(domain.vapidPublicKey)
            .setWhitelistDomains(parseArrayOrNull(domain.whitelistDomains))

        if (domain.globalPromptSettings) {
            builder.setGlobalPromptSettings(domain.globalPromptSettings)
        }
        if (domain.ecommConfig) {
            builder.setEcommConfig(domain.ecommConfig)
        }
        if (domain.mediaQuality) {
            builder.setMediaQuality(domain.mediaQuality)
        }
    } else {
        if (!isJSONObjectType(domain)) {
            throw new HydrationException(domain, Domain)
        }
        builder
            .setId(domain.id)
            .setName(domain.name)
            .setAccountId(domain.account_id)
            .setAccountName(domain.account_name)
            .setAccountFlags(domain.account_flags)
            .setAddedNotificationQueryParams(domain.added_notification_query_params)
            .setCreatedAt(domain.created_at)
            .setCreatedBy(domain.created_by)
            .setCreatedByUsername(domain.created_by_user_name)
            .setCollapseKeyType(domain.collapse_key_type)
            .setCurrency(domain.currency!)
            .setCurrencyCode(domain.currency_code)
            .setDefaultNotificationActionLabels(domain.default_notification_action_labels)
            .setDefaultNotificationActions(
                domain.default_notification_actions?.map((action: any) =>
                    hydrateNotificationActionModelFromDto(action),
                ) ?? null,
            )
            .setDefaultNotificationTtlSeconds(domain.default_notification_ttl_seconds)
            .setDeliveryThrottleMaxSeconds(domain.delivery_throttle_max_seconds)
            /**
             * Read under both casings. This branch takes plain objects, which reach it
             * from two places: DomainDto, which declares `duplicate_time_window`, and raw
             * API responses, which are camelCase. `from` returns undefined for undefined
             * input, so reading only the snake_case key would silently discard a
             * camelCase payload - and with it the nested landingUrlMatch config, leaving
             * the settings UI showing defaults instead of what the domain has persisted.
             */
            .setDuplicateTimeWindow(
                DomainDuplicateTimeWindow.from(domain.duplicate_time_window ?? domain.duplicateTimeWindow),
            )
            .setDefaultIconUrl(domain.default_icon_url)
            .setDefaultBadgeUrl(domain.default_badge_url)
            .setDisplayMeta(domain.display_meta)
            .setDisplayName(domain.display_name)
            .setDomainKey(domain.domain_key)
            .setFcmServerKey(domain.fcm_server_key)
            .setFlags(domain.flags)
            .setFrequencyCaps(hydrateDomainFrequencyCaps(domain.frequency_caps))
            .setNotificationFrequencyCaps(
                domain.notification_frequency_caps?.map(hydrateNotificationFrequencyCaps) ?? null,
            )
            .setLandingUrlDomainWhitelist(domain.landing_url_domain_whitelist)
            .setApnsConfiguration(hydrateWebApnsConfigurationModel(domain.apns_configuration))
            .setNativeApnsConfiguration(hydrateNativeApnsConfigurationModel(domain.native_apns_configuration))
            .setNativeFcmConfiguration(hydrateNativeFcmConfigurationModel(domain.native_fcm_configuration))
            .setNotificationFieldSettings(domain.notification_field_settings ?? null)
            .setProdDomainId(domain.prod_domain_id)
            .setRoutingKey(domain.routing_key)
            .setSdkEventOnlyDomains(domain.sdk_event_only_domains)
            .setSenderId(domain.sender_id)
            .setStatus(domain.status)
            .setStatusId(domain.status_id)
            .setTimeZone(domain.timezone)
            .setUpdatedAt(domain.updated_at)
            .setUpdatedBy(domain.updated_by)
            .setUpdatedByUsername(domain.updated_by_user_name)
            .setUtmParams(hydrateUtmParams(domain.utm_params))
            .setVapidPublicKey(domain.vapid_public_key)
            .setWhitelistDomains(domain.whitelist_domains)

        if (domain.global_prompt_settings?.referrers) {
            const globalPromptSettings = DomainGlobalPromptSettings.Builder.setReferrers(
                hydrateGlobalPromptReferrerConditions(domain.global_prompt_settings.referrers),
            )

            builder.setGlobalPromptSettings(globalPromptSettings.build())
        }
        if (domain.ecomm_config) {
            const config = domain.ecomm_config
            const ecommConfig = DomainEcommConfiguration.Builder.setItemType(config.item_type)
                .setMaxAbandonedItemAgeDays(config.max_abandoned_item_age_days)
                .setClickAttributionWindowHours(config.click_attribution_window_hours)
                .setImpressionAttributionWindowHours(config.click_impressions_attribution_window_hours)
                .build()

            builder.setEcommConfig(ecommConfig)
        }
        if (domain.media_quality) {
            builder.setMediaQuality(domain.media_quality)
        }
    }

    const hydrated = builder.build()

    // NOTE: Domain.Builder.build() runs plainToInstance(Domain, this.data) with no options. Because
    // notificationFieldSettings is declared with @Expose({ name: 'notification_field_settings' }) +
    // @WithDefaultValue(null), build() reads the absent snake_case key and resets the value to null,
    // silently dropping what setNotificationFieldSettings(...) stored under the camelCase key. The
    // built Domain is mutable, so re-assign the builder's already-converted value to guarantee it is
    // carried through (preserving each source's null-vs-array distinction).
    hydrated.notificationFieldSettings = builder.notificationFieldSettings

    return hydrated
}

export function hydrateGlobalPromptReferrerConditions(
    settings?: IGlobalPromptReferrerConditions,
): GlobalPromptReferrerConditions {
    return GlobalPromptReferrerConditions.Builder.setEnabled(settings?.enabled ?? false)
        .setPersistTtlSeconds(settings?.persist_ttl_seconds ?? 60 * 60 * 24 * 7)
        .setWhenSelf(settings?.when_self ?? PromptReferrerAllowType.IGNORE)
        .setWhenBlank(settings?.when_blank ?? PromptReferrerAllowType.ALLOW)
        .setAllowed(settings?.allowed)
        .setBlocked(settings?.blocked)
        .build()
}

export function hydrateNotificationFrequencyCaps(
    cap: NotificationFrequencyCapsConfig | NotificationFrequencyCapsConfigBuilder | any,
) {
    if (cap === null) {
        return null
    }

    const builder = NotificationFrequencyCapsConfig.Builder

    if (cap instanceof NotificationFrequencyCapsConfig || cap instanceof NotificationFrequencyCapsConfigBuilder) {
        builder.setValue(cap.value).setDurationMinutes(cap.durationMinutes).setDisplayMetric(cap.displayMetric)
    } else {
        if (!isJSONObjectType(cap)) {
            throw new HydrationException(cap, NotificationFrequencyCapsConfig)
        }

        builder.setValue(cap.value).setDurationMinutes(cap.duration_minutes).setDisplayMetric(cap.display_metric)
    }

    return builder.build()
}

export function hydrateDomainFrequencyCaps(
    caps: DomainFrequencyCaps | DomainFrequencyCapsBuilder | IFrequencyCapsConfig | null | any,
) {
    if (caps === null) {
        return null
    }

    const builder = DomainFrequencyCaps.Builder
    if (!isJSONObjectType(caps)) {
        throw new HydrationException(caps, DomainFrequencyCaps)
    }

    if (caps.prompt) {
        builder.setPrompt(hydrateDomainFrequencyCap(caps.prompt))
    }
    if (caps.notification) {
        builder.setNotification(hydrateDomainFrequencyCap(caps.notification))
    }

    return builder.build()
}

export function hydrateDomainFrequencyCap(cap: DomainFrequencyCap | DomainFrequencyCapBuilder | IFrequencyCap | any) {
    const builder = DomainFrequencyCap.Builder
    if (cap instanceof DomainFrequencyCap || cap instanceof DomainFrequencyCapBuilder) {
        builder.setOccurrences(cap.occurrences).setFcapSeconds(cap.fcapSeconds).setDisplayMetric(cap.displayMetric)
    } else {
        if (!isJSONObjectType(cap)) {
            throw new HydrationException(cap, DomainFrequencyCap)
        }
        builder
            .setOccurrences(parseInt(cap.occurrences, 10))
            .setFcapSeconds(parseInt(cap.fcap_seconds, 10))
            .setDisplayMetric(cap.display_metric)
    }

    return builder.build()
}

// TODO: Either update model to allow usePrimaryLandingUrl to be undefined or write migration to fix data
export function hydrateNotificationActionModelFromDto(
    action: NotificationAction | NotificationActionBuilder | INotificationAction | any,
) {
    let builder = NotificationAction.Builder
    if (action instanceof NotificationAction) {
        builder
            .setId(action.id)
            .setType(action.type)
            .setLabel(action.label)
            .setOrdinal(action.ordinal)
            .setLandingUrl(action.landing_url)
            .setUsePrimaryLandingUrl(action.use_primary_landing_url)
            .setDisplayMeta(action.display_meta)
    } else if (action instanceof NotificationActionBuilder) {
        builder
            .setId(action.id)
            .setType(action.type)
            .setLabel(action.label)
            .setOrdinal(action.ordinal)
            .setLandingUrl(action.landingUrl)
            .setUsePrimaryLandingUrl(action.usePrimaryLandingUrl)
            .setDisplayMeta(action.displayMeta)
    } else {
        if (!isJSONObjectType(action)) {
            throw new HydrationException(action, NotificationAction)
        }
        if ('use_primary_landing_url' in action) {
            builder
                .setId(action.id)
                .setType(action.type)
                .setLabel(action.label)
                .setOrdinal(action.ordinal)
                .setLandingUrl(action.landing_url)
                .setUsePrimaryLandingUrl(action.type === NotificationActionType.CLOSE ? undefined : !action.landing_url)
                .setDisplayMeta(action.display_meta)
        } else {
            builder
                .setId(action.id)
                .setType(action.type)
                .setLabel(action.label)
                .setOrdinal(action.ordinal)
                .setLandingUrl(action.landingUrl)
                .setUsePrimaryLandingUrl(action.type === NotificationActionType.CLOSE ? undefined : !action.landingUrl)
                .setDisplayMeta(action.displayMeta)
        }
    }

    return builder.build()
}

const hydrateUtmParams = (params: DomainUtmParams | DomainUtmParamsBuilder | any): DomainUtmParams | null => {
    if (params === null) return null

    let builder = DomainUtmParams.Builder
    if (params instanceof DomainUtmParams || params instanceof DomainUtmParamsBuilder) {
        builder
            .setUtmSource(params.utmSource)
            .setUtmContent(params.utmContent)
            .setUtmMedium(params.utmMedium)
            .setUtmCampaign(params.utmCampaign)
            .setUtmTerm(params.utmTerm)
    } else {
        if (!isJSONObjectType(params)) {
            throw new HydrationException(params, DomainUtmParams)
        }
        if ('utmSource' in params) {
            builder
                .setUtmSource(params.utmSource)
                .setUtmContent(params.utmContent)
                .setUtmMedium(params.utmMedium)
                .setUtmCampaign(params.utmCampaign)
                .setUtmTerm(params.utmTerm)
        } else {
            builder
                .setUtmSource(params.utm_source)
                .setUtmContent(params.utm_content)
                .setUtmMedium(params.utm_medium)
                .setUtmCampaign(params.utm_campaign)
                .setUtmTerm(params.utm_term)
        }
    }

    return builder.build()
}

// Send Integration Configuration Hydrators

export function hydrateWebApnsConfigurationModel(
    config: WebApnsSendIntegrationConfig | WebApnsSendIntegrationConfigBuilder | IWebApnsSendIntegration | any,
): WebApnsSendIntegrationConfig {
    const builder = WebApnsSendIntegrationConfig.Builder
    if (config instanceof WebApnsSendIntegrationConfig) {
        if (config.isActive) {
            builder
                .setIsActive(config.isActive)
                // when isActive is TRUE - these will have been validated by build() method
                .setPrivateKey(config.privateKey!)
                .setPushPackage(config.pushPackage!)
                .setExpiration(config.expiration)
        } else {
            builder.setIsActive(false)
        }
    } else if (config instanceof WebApnsSendIntegrationConfigBuilder) {
        if (config.isActive) {
            builder
                .setIsActive(config.isActive)
                .setPrivateKey(config.privateKey)
                .setPushPackage(config.pushPackage)
                .setExpiration(config.expiration)
        } else {
            builder.setIsActive(false)
        }
    } else {
        if (!isJSONObjectType(config)) {
            throw new Error()
        }
        if (config.isActive) {
            let privateKey = SendIntegrationPrivateKey.Builder
            if (config.privateKey) {
                if ('privateKeyFileUrl' in config.privateKey) {
                    privateKey.setPrivateKeyFileUrl(config.privateKey.privateKeyFileUrl)
                } else if ('private_key_file_url' in config.privateKey) {
                    privateKey.setPrivateKeyFileUrl(config.privateKey.private_key_file_url)
                }
                if ('privateKeyPassPhrase' in config.privateKey) {
                    privateKey.setPrivateKeyPassPhrase(config.privateKey.privateKeyPassPhrase)
                } else if ('private_key_pass_phrase' in config.privateKey) {
                    privateKey.setPrivateKeyPassPhrase(config.privateKey.private_key_pass_phrase)
                }
            }

            let pushPackage = SendIntegrationPushPackage.Builder
            if (config.pushPackage) {
                if ('websitePushId' in config.pushPackage) {
                    pushPackage.setWebsitePushId(config.pushPackage.websitePushId)
                } else if ('website_push_id' in config.pushPackage) {
                    pushPackage.setWebsitePushId(config.pushPackage.website_push_id)
                }

                if ('iconSourceUrl' in config.pushPackage) {
                    pushPackage.setIconSourceUrl(config.pushPackage.iconSourceUrl)
                } else if ('icon_source_url' in config.pushPackage) {
                    pushPackage.setIconSourceUrl(config.pushPackage.icon_source_url)
                }
            }
            builder
                .setIsActive(config.isActive)
                .setPrivateKey(privateKey.build())
                .setPushPackage(pushPackage.build())
                .setExpiration(config.expiration)
        } else {
            builder.setIsActive(false)
        }
    }

    return builder.build()
}

export function hydrateNativeApnsConfigurationModel(
    config:
        | NativeApnsSendIntegrationConfig
        | NativeApnsSendIntegrationConfigBuilder
        | NativeP12ApnsConfiguration
        | NativeP8ApnsConfiguration
        | IAbstractNativeApnsSendIntegration
        | any,
): NativeApnsSendIntegrationConfig {
    let model: NativeApnsSendIntegrationConfig
    if (config instanceof NativeApnsSendIntegrationConfig) {
        if (config.isActive) {
            if (config.privateKeyType === SendIntegrationPrivateKeyType.P8) {
                model = NativeApnsSendIntegrationConfig.buildActiveP8Configuration(config)
            } else {
                model = NativeApnsSendIntegrationConfig.buildActiveP12Configuration(config)
            }
        } else {
            model = NativeApnsSendIntegrationConfig.Builder.setIsActive(false).build()
        }
    } else if (config instanceof NativeApnsSendIntegrationConfigBuilder) {
        if (config.isActive) {
            if (config.privateKeyType === SendIntegrationPrivateKeyType.P8) {
                model = NativeApnsSendIntegrationConfig.buildActiveP8Configuration(config)
            } else {
                model = NativeApnsSendIntegrationConfig.buildActiveP12Configuration(config)
            }
        } else {
            model = NativeApnsSendIntegrationConfig.Builder.setIsActive(false).build()
        }
    } else if (config instanceof NativeP12ApnsConfiguration || config instanceof NativeP8ApnsConfiguration) {
        if (config.getIsActive()) {
            // type won't be inferred from privateKeyType === SendIntegrationKeyType because these are extended
            // from AbstractNativeApnsSendIntegration class that allows both...
            if (
                config.getPrivateKeyType() === SendIntegrationPrivateKeyType.P8 ||
                config instanceof NativeP8ApnsConfiguration
            ) {
                model = NativeApnsSendIntegrationConfig.buildActiveP8Configuration({
                    teamId: (config as NativeP8ApnsConfiguration).getTeamId(),
                    keyId: (config as NativeP8ApnsConfiguration).getKeyId(),
                    appBundleId: config.getAppBundleId(),
                    privateKeyFileUrl: config.getPrivateKeyFileUrl(),
                    additionalPermissions: config.getAdditionalPermissions(),
                    apnsEnvironment: config.getApnsEnvironment(),
                })
            } else {
                model = NativeApnsSendIntegrationConfig.buildActiveP12Configuration({
                    appBundleId: config.getAppBundleId(),
                    privateKeyFileUrl: config.getPrivateKeyFileUrl(),
                    privateKeyPassPhrase: config.getPrivateKeyPassPhrase(),
                    expiration: config.getExpiration(),
                    additionalPermissions: config.getAdditionalPermissions(),
                    apnsEnvironment: config.getApnsEnvironment(),
                })
            }
        } else {
            model = NativeApnsSendIntegrationConfig.Builder.setIsActive(false).build()
        }
    } else {
        if (!isJSONObjectType(config)) {
            throw new HydrationException(config, NativeApnsSendIntegrationConfig)
        }

        if (config.isActive) {
            if (config.privateKeyType === SendIntegrationPrivateKeyType.P8) {
                model = NativeApnsSendIntegrationConfig.buildActiveP8Configuration({
                    teamId: config.teamId,
                    keyId: config.keyId,
                    appBundleId: config.appBundleId,
                    privateKeyFileUrl: config.privateKeyFileUrl,
                    additionalPermissions: config.additionalPermissions,
                    apnsEnvironment: config.apnsEnvironment,
                })
            } else {
                model = NativeApnsSendIntegrationConfig.buildActiveP12Configuration({
                    appBundleId: config.appBundleId,
                    privateKeyFileUrl: config.privateKeyFileUrl,
                    privateKeyPassPhrase: config.privateKeyPassPhrase,
                    expiration: config.expiration,
                    additionalPermissions: config.additionalPermissions,
                    apnsEnvironment: config.apnsEnvironment,
                })
            }
        } else if (config.is_active) {
            if (config.private_key_type === SendIntegrationPrivateKeyType.P8) {
                model = NativeApnsSendIntegrationConfig.buildActiveP8Configuration({
                    teamId: config.team_id,
                    keyId: config.key_id,
                    appBundleId: config.app_bundle_id,
                    privateKeyFileUrl: config.private_key_file_url,
                    additionalPermissions: config.additional_permissions,
                    apnsEnvironment: config.apns_environment,
                })
            } else {
                model = NativeApnsSendIntegrationConfig.buildActiveP12Configuration({
                    appBundleId: config.app_bundle_id,
                    privateKeyFileUrl: config.private_key_file_url,
                    privateKeyPassPhrase: config.private_key_pass_phrase,
                    expiration: config.expiration,
                    additionalPermissions: config.additional_permissions,
                    apnsEnvironment: config.apns_environment,
                })
            }
        } else {
            model = NativeApnsSendIntegrationConfig.Builder.setIsActive(false).build()
        }
    }

    return model
}

export function hydrateNativeFcmConfigurationModel(
    config:
        | NativeFcmSendIntegrationConfig
        | NativeFcmSendIntegrationConfigBuilder
        | NativeV1FcmConfiguration
        | NativeLegacyFcmConfiguration
        | IAbstractNativeFcmSendIntegration
        | any,
): NativeFcmSendIntegrationConfig {
    let model: NativeFcmSendIntegrationConfig
    if (config instanceof NativeFcmSendIntegrationConfig) {
        if (config.isActive) {
            if (config.apiVersion === FcmSendIntegrationApiVersion.V1) {
                model = NativeFcmSendIntegrationConfig.buildActiveV1Configuration(config)
            } else {
                model = NativeFcmSendIntegrationConfig.buildActiveLegacyConfiguration(config)
            }
        } else {
            model = NativeFcmSendIntegrationConfig.Builder.setIsActive(false).build()
        }
    } else if (config instanceof NativeFcmSendIntegrationConfigBuilder) {
        if (config.isActive) {
            if (config.apiVersion === FcmSendIntegrationApiVersion.V1) {
                model = NativeFcmSendIntegrationConfig.buildActiveV1Configuration(config)
            } else {
                model = NativeFcmSendIntegrationConfig.buildActiveLegacyConfiguration(config)
            }
        } else {
            model = NativeFcmSendIntegrationConfig.Builder.setIsActive(false).build()
        }
    } else if (config instanceof NativeV1FcmConfiguration || config instanceof NativeLegacyFcmConfiguration) {
        if (config.getIsActive()) {
            if (config instanceof NativeV1FcmConfiguration) {
                model = NativeFcmSendIntegrationConfig.buildActiveV1Configuration({
                    senderId: config.getSenderId() ?? null,
                    serviceAccountJsonFileUrl: config.getServiceAccountJsonFileUrl() ?? null,
                })
            } else {
                model = NativeFcmSendIntegrationConfig.buildActiveLegacyConfiguration({
                    serverApiKey: config.getServerApiKey() ?? null,
                    senderId: config.getSenderId() ?? null,
                })
            }
        } else {
            model = NativeFcmSendIntegrationConfig.Builder.setIsActive(false).build()
        }
    } else {
        if (!isJSONObjectType(config)) {
            throw new HydrationException(config, NativeFcmSendIntegrationConfig)
        }
        if (config.isActive) {
            if (config.apiVersion === FcmSendIntegrationApiVersion.V1) {
                model = NativeFcmSendIntegrationConfig.buildActiveV1Configuration({
                    senderId: config.senderId,
                    serviceAccountJsonFileUrl: config.serviceAccountJsonFileUrl,
                })
            } else {
                model = NativeFcmSendIntegrationConfig.buildActiveLegacyConfiguration({
                    serverApiKey: config.serverApiKey,
                    senderId: config.senderId,
                })
            }
        } else if (config.is_active) {
            if (config.api_version === FcmSendIntegrationApiVersion.V1) {
                model = NativeFcmSendIntegrationConfig.buildActiveV1Configuration({
                    senderId: config.sender_id,
                    serviceAccountJsonFileUrl: config.service_account_json_file_url,
                })
            } else {
                model = NativeFcmSendIntegrationConfig.buildActiveLegacyConfiguration({
                    serverApiKey: config.server_api_key,
                    senderId: config.sender_id,
                })
            }
        } else {
            model = NativeFcmSendIntegrationConfig.Builder.setIsActive(false).build()
        }
    }
    return model
}

export function hydrateFeedModel(feed: Feed | any): Feed {
    let builder = new MutableFeed()
    if (feed instanceof Feed) {
        builder = feed.toMutable()
    } else {
        if (!isJSONObjectType(feed)) {
            throw new HydrationException(feed, Feed)
        }
        builder = MutableFeed.parseJsonObject(feed)
    }

    if (feed.config.fieldOptions) {
        builder.config.fieldOptions = MutableFeedConfigurationFieldOptions.parseJsonObject(feed.config.fieldOptions)
    }

    if (feed.config.articleMaxAge) {
        builder.config.articleMaxAge.displayMetric = enumFromValue(
            RelativeDateDisplayMetric,
            feed.config.articleMaxAge.displayMetric,
        )
    }
    if (feed.config.notificationLifespan) {
        builder.config.notificationLifespan.displayMetric = enumFromValue(
            RelativeDateDisplayMetric,
            feed.config.notificationLifespan.displayMetric,
        )
    }
    if ((feed.config.schedule as AsapFeedSchedule).minFrequency) {
        ;(builder.config.schedule as MutableAsapFeedSchedule).minFrequency.displayMetric = enumFromValue(
            RelativeDateDisplayMetric,
            (feed.config.schedule as AsapFeedSchedule).minFrequency.displayMetric,
        )
    }
    if ((feed.config.schedule as FixedFeedSchedule).deliveryDelay) {
        ;(builder.config.schedule as MutableFixedFeedSchedule).deliveryDelay.displayMetric = enumFromValue(
            RelativeDateDisplayMetric,
            (feed.config.schedule as FixedFeedSchedule).deliveryDelay.displayMetric,
        )
    }

    return builder.validate()
}

export function hydrateIndustryModel(industry: Industry | any) {
    let builder = new MutableIndustry()

    if (industry instanceof Industry) {
        builder = industry.toMutable()
    } else {
        if (!isJSONObjectType(industry)) {
            throw new HydrationException(industry, Industry)
        }
        builder = MutableIndustry.parseJsonObject(industry)
    }

    builder.id = industry.id
    builder.name = industry.name

    return builder.validate()
}
